import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
    getPageVariablesBatch,
    bulkUpsertPageVariables,
    type CampanhaDuplicadaGrupo,
} from '@/services/api/connections';
import type { PageVariable, PageVariableInput } from '../types/meta.types';
import { useToast } from '@/hooks/use-toast';

/**
 * Ajuste da campanha das páginas de um grupo, sem abrir página por página.
 *
 * O SALVAMENTO É UM REPLACE, E ISSO IMPORTA
 * `PUT /pages/{id}/variables` substitui TODAS as variáveis da página pela
 * lista enviada. Mandar só a campanha apagaria DOMAIN, UTM_SOURCE e o resto.
 * Por isso o modal carrega as variáveis completas de cada página (uma chamada
 * em lote), troca só o valor da chave do grupo e devolve o conjunto inteiro.
 *
 * Só escreve nas páginas cujo valor mudou — quem ficou igual não é tocado,
 * para não gerar escrita inútil nem mexer em `updated_at` sem motivo.
 */

interface Props {
    grupo: CampanhaDuplicadaGrupo | null;
    onClose: () => void;
    onSaved: () => void;
}

export default function CampanhaDuplicadaFixModal({ grupo, onClose, onSaved }: Props) {
    const { toast } = useToast();
    const [valores, setValores] = useState<Record<number, string>>({});
    const [salvando, setSalvando] = useState(false);

    const pageIds = useMemo(() => (grupo?.paginas ?? []).map(p => p.id), [grupo]);

    const { data: variaveisPorPagina, isLoading } = useQuery({
        queryKey: ['variables-batch', pageIds],
        queryFn: () => getPageVariablesBatch(pageIds),
        enabled: !!grupo && pageIds.length > 0,
    });

    useEffect(() => {
        if (!grupo) { setValores({}); return; }
        const inicial: Record<number, string> = {};
        for (const p of grupo.paginas) inicial[p.id] = grupo.campanha;
        setValores(inicial);
    }, [grupo]);

    if (!grupo) return null;

    const alterados = grupo.paginas.filter(p => (valores[p.id] ?? '') !== grupo.campanha);
    // Trocar todas para o mesmo valor novo não resolve nada — continuariam
    // dividindo, só que sob outro nome.
    const aindaDuplicado = (() => {
        const contagem = new Map<string, number>();
        for (const p of grupo.paginas) {
            const v = (valores[p.id] ?? '').trim();
            if (v) contagem.set(v, (contagem.get(v) ?? 0) + 1);
        }
        return [...contagem.values()].some(n => n > 1);
    })();
    const vazios = grupo.paginas.some(p => !(valores[p.id] ?? '').trim());

    const salvar = async () => {
        if (!variaveisPorPagina) return;
        setSalvando(true);
        let gravadas = 0;
        try {
            for (const p of alterados) {
                const atuais = (variaveisPorPagina[String(p.id)] ?? []) as PageVariable[];
                const novoValor = (valores[p.id] ?? '').trim();
                let achou = false;
                const payload: PageVariableInput[] = atuais.map(v => {
                    if (v.key !== grupo.chave) return { key: v.key, value: v.value, dynamism: v.dynamism };
                    achou = true;
                    return { key: v.key, value: novoValor, dynamism: v.dynamism };
                });
                // Página do grupo sem a chave não deveria existir, mas se o
                // cadastro mudar entre a verificação e o salvamento, cria em
                // vez de perder a edição em silêncio.
                if (!achou) payload.push({ key: grupo.chave, value: novoValor, dynamism: false });
                await bulkUpsertPageVariables(p.id, payload);
                gravadas += 1;
            }
            toast({
                title: 'Campanhas ajustadas',
                description: `${gravadas} página(s) atualizadas. A receita passa a ser medida separadamente.`,
            });
            onSaved();
        } catch (e: any) {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: e?.response?.data?.detail
                    || `Falhou depois de gravar ${gravadas} de ${alterados.length}.`,
            });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Dialog open={!!grupo} onOpenChange={o => { if (!o) onClose(); }}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Ajustar campanha compartilhada</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                        <span className="font-medium text-gray-800">{grupo.chave}</span> com o valor{' '}
                        <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border">
                            {grupo.campanha}
                        </code>{' '}
                        está em {grupo.qtd_paginas} páginas. Enquanto for assim, a origem devolve um
                        número só e cada página fica com 1/{grupo.qtd_paginas} da receita.
                        {grupo.canal === 'flow' && (
                            <> Em flow isso é sempre erro — a campanha deveria ser uma por página.</>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-8 text-gray-500">
                            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Carregando variáveis…
                        </div>
                    ) : (
                        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                            {grupo.paginas.map(p => (
                                <div key={p.id} className="flex items-center gap-3">
                                    <div className="w-52 flex-shrink-0 min-w-0">
                                        <div className="text-sm text-gray-800 truncate">{p.nome}</div>
                                        <div className="text-[11px] text-gray-400 truncate">
                                            {p.idioma || '—'}
                                            {!p.ativa && <span className="ml-1 text-amber-600">· pausada</span>}
                                        </div>
                                    </div>
                                    <Input
                                        value={valores[p.id] ?? ''}
                                        onChange={e => setValores(v => ({ ...v, [p.id]: e.target.value }))}
                                        className="font-mono text-xs"
                                        placeholder="valor da campanha"
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {aindaDuplicado && (
                        <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            <span>
                                Ainda há páginas com o mesmo valor. Elas continuarão dividindo a
                                receita — só mudaria o nome da campanha.
                            </span>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={salvando}>Cancelar</Button>
                    <Button
                        onClick={salvar}
                        disabled={salvando || isLoading || alterados.length === 0 || vazios}
                    >
                        {salvando && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                        {alterados.length === 0
                            ? 'Nada alterado'
                            : `Salvar ${alterados.length} página(s)`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
